/**
 * PDF 表格分頁共用邏輯（發票 / 報價 / 收據）。
 *
 * 表格高度係固定的，所以分頁必須量準每一列的實際高度：
 * 1. `.pdf-item` 係 flex 列，高度由最高嗰個欄位決定，唔可以淨係計摘要欄；
 * 2. 任何一列本身高過一頁時，要拆成續頁列，否則會衝出表格覆蓋總額區。
 *
 * 各單據的欄位寬度同可用高度唔同，由呼叫方以 options 傳入。
 */

export const ROW_LINE_HEIGHT = 20; // .pdf-item line-height
export const ROW_BORDER_HEIGHT = 1; // .pdf-item border-bottom

// 同一次分頁內重複出現的儲存格（例如相同金額）唔使重複觸發 layout
const measureCache = new Map();

export function clearMeasureCache() {
  measureCache.clear();
}

/**
 * 量度單一儲存格的高度。寬度、字型、行高必須同正式表格一致，否則分頁會算錯。
 *
 * @param {string} html 該欄的 HTML（同 v-html render 出嚟嘅一致）
 * @param {number} width 該欄的內容寬度（wrapper 寬度扣除 padding）
 * @returns {number} 高度（px）；空內容回傳 0
 */
export function measureCellHeight(html, width) {
  if (html === null || html === undefined || html === "") {
    return 0;
  }

  const cacheKey = `${width}|${html}`;
  if (measureCache.has(cacheKey)) {
    return measureCache.get(cacheKey);
  }

  const tempDiv = document.createElement("div");
  tempDiv.style.cssText = [
    "position: absolute",
    "visibility: hidden",
    "left: -9999px",
    "top: 0",
    `width: ${width}px`,
    "font-size: 14px",
    `line-height: ${ROW_LINE_HEIGHT}px`,
    "white-space: pre-wrap",
  ].join("; ");

  tempDiv.innerHTML = html;
  document.body.appendChild(tempDiv);

  const height = Math.ceil(tempDiv.offsetHeight);

  document.body.removeChild(tempDiv);

  measureCache.set(cacheKey, height);

  return height;
}

/**
 * 一列的高度 = 最高嗰個欄位 + 底線，並且唔低過一行。
 *
 * @param {Array<{ html: string, width: number }>} cells 該列會顯示的欄位
 */
export function measureRowHeight(cells) {
  const heights = cells
    .filter((cell) => cell && cell.html !== null && cell.html !== undefined)
    .map((cell) => measureCellHeight(cell.html, cell.width));

  return Math.max(ROW_LINE_HEIGHT, ...heights, 0) + ROW_BORDER_HEIGHT;
}

function toHtml(text) {
  return String(text === null || text === undefined ? "" : text).replace(
    /\n/g,
    "<br>"
  );
}

function countWrappedLines(text, width) {
  return measureCellHeight(toHtml(text), width) / ROW_LINE_HEIGHT;
}

/**
 * 用二分搜尋搵出放得落 maxLines 的最長前綴。
 * 至少保留一個字元，避免無法斷行的長字串令呼叫方死循環。
 */
function takeTextThatFits(text, width, maxLines) {
  let low = 1;
  let high = text.length;
  let best = 1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);

    if (countWrappedLines(text.slice(0, mid), width) <= maxLines) {
      best = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return text.slice(0, best);
}

/**
 * 把一段文字拆成每段唔超過 maxLines 行。先按 \n 貪心裝行，
 * 單一行本身都放唔落時再按字元切開。
 *
 * @returns {string[]} 至少一段
 */
export function splitTextToChunks(text, width, maxLines) {
  const chunks = [];
  let current = [];
  let currentLines = 0;

  const flush = () => {
    if (current.length > 0) {
      chunks.push(current.join("\n"));
      current = [];
      currentLines = 0;
    }
  };

  String(text === null || text === undefined ? "" : text)
    .split("\n")
    .forEach((line) => {
      let remaining = line;

      for (;;) {
        const lines = countWrappedLines(remaining, width);

        if (currentLines + lines <= maxLines) {
          current.push(remaining);
          currentLines += lines;
          return;
        }

        if (currentLines > 0) {
          // 當前段仲有內容，先收口再用新一段試過
          flush();
        } else {
          const head = takeTextThatFits(remaining, width, maxLines);
          chunks.push(head);
          remaining = remaining.slice(head.length);

          if (!remaining) {
            return;
          }
        }
      }
    });

  flush();

  return chunks.length > 0 ? chunks : [""];
}

/**
 * 把列分頁。高過一頁的列會先拆成續頁列。
 *
 * @param {Object} options
 * @param {Array} options.rows 已排好序的列
 * @param {number} options.bodyHeight 表格扣除表頭後可容納的列高
 * @param {number} options.descriptionWidth 摘要欄的內容寬度，用嚟拆長文字
 * @param {(row: Object) => number} options.measureRow 量度整列高度
 * @param {(row: Object) => string} options.getDescription 取出摘要純文字
 * @param {(row: Object, text: string, index: number) => Object} options.withDescription
 *        產生第 index 段的列物件（index 0 為首段，其餘為續頁列）
 * @returns {Array<Array<Object>>} 每頁一個陣列
 */
export function buildPages({
  rows,
  bodyHeight,
  descriptionWidth,
  measureRow,
  getDescription,
  withDescription,
}) {
  clearMeasureCache();

  const maxLines = Math.max(
    1,
    Math.floor((bodyHeight - ROW_BORDER_HEIGHT) / ROW_LINE_HEIGHT)
  );

  const entries = [];

  rows.forEach((row) => {
    const height = measureRow(row);

    if (height <= bodyHeight) {
      entries.push({ row, height });
      return;
    }

    splitTextToChunks(
      getDescription(row),
      descriptionWidth,
      maxLines
    ).forEach((chunk, index) => {
      const part = withDescription(row, chunk, index);
      entries.push({ row: part, height: measureRow(part) });
    });
  });

  const pages = [];
  let currentPage = [];
  let currentHeight = 0;

  entries.forEach((entry) => {
    if (currentPage.length > 0 && currentHeight + entry.height > bodyHeight) {
      pages.push(currentPage);
      currentPage = [];
      currentHeight = 0;
    }

    currentPage.push(entry.row);
    currentHeight += entry.height;
  });

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return pages;
}
